import { notifications } from "@mantine/notifications";
import type { MRT_RowData, MRT_TableInstance } from "mantine-react-table-open";
import { MRT_ToggleFullScreenButton } from "mantine-react-table-open";
import { useEffect, useRef } from "react";
import { NotificationPresets } from "../../../mantine/mantinePresets";

interface ToggleFullscreenButtonProps<TData extends MRT_RowData> {
  table: MRT_TableInstance<TData>;
}

function ToggleFullscreenButton<TData extends MRT_RowData>({
  table
}: ToggleFullscreenButtonProps<TData>) {
  const { isFullScreen } = table.getState();
  // Save non-fullscreen padding to restore it when exiting fullscreen mode
  const tablePaper = table.refs.tablePaperRef?.current;
  const initialPadding = useRef(tablePaper?.style.paddingInline);

  useEffect(() => {
    if (!isFullScreen) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") table.setIsFullScreen(false);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [table, isFullScreen]);

  useEffect(() => {
    const setTablePadding = (padding: string) => {
      const tablePaper = table.refs.tablePaperRef?.current;
      if (tablePaper) {
        tablePaper.style.paddingInline = padding;
        tablePaper.style.transition = "unset";
      }
    };

    // Capture the initial padding value at the start of the effect
    const initialPaddingValue = initialPadding.current || "0";

    if (isFullScreen) {
      notifications.show({
        color: NotificationPresets.info.color,
        position: "top-center",
        message: "Vollbildmodus. Zum Beenden ESC drücken.",
        id: "fullscreen-notification"
      });
      const tablePaper = table.refs.tablePaperRef?.current;
      if (tablePaper) {
        tablePaper.style.paddingInline = "var(--mantine-spacing-md)";
        tablePaper.style.transition = "unset";
      }
    } else {
      notifications.hide("fullscreen-notification");
      setTablePadding(initialPaddingValue);
    }

    return () => {
      notifications.hide("fullscreen-notification");
      setTablePadding(initialPaddingValue);
    };
  }, [isFullScreen, table.refs.tablePaperRef]);

  return (
    <MRT_ToggleFullScreenButton
      table={table}
      title={isFullScreen ? "Vollbild deaktivieren" : "Vollbild aktivieren"}
    />
  );
}

export { ToggleFullscreenButton };
